import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, Signal } from '@angular/core';
import { List, ListItem } from '@app/_interfaces/list.interface';
import { CacheService } from '@app/_services/cache.service';
import { MapService } from '@app/_services/map.service';
import { SettingsService } from '@app/_services/settings.service';
import { ListStore, UserResourceStore } from '@app/_stores';
import { CollaboraterRole } from '@app/_types/generic.type';
import { getRoleByUid, getCollaboratorCount } from '@app/_utils/members-model.utils';
import { MembersModel } from '@app/_interfaces/generic.interface';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { ListItemsComponent } from '../list-items/list-items.component';
import { MatDialog } from '@angular/material/dialog';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { ModalService } from '@app/_services/modal.service';
import { SmartTimeDiffPipe } from '@app/_pipes/smart-time-diff.pipe';
import { FavoriteIndicatorComponent } from '@app/components/shared/favorite-indicator/favorite-indicator.component';
import { PublicIndicatorComponent } from '@app/components/shared/public-indicator/public-indicator.component';

@Component({
  selector: 'app-list-block',
  imports: [
    CommonModule,
    SvgComponent,
    MeUserAvatarComponent,
    FavoriteIndicatorComponent,
    PublicIndicatorComponent,
    SmartTimeDiffPipe
  ],
  templateUrl: './list-block.component.html',
  styleUrl: './list-block.component.scss'
})

export class ListBlockComponent {

  readonly listStore = inject(ListStore);
  readonly userResourceStore = inject(UserResourceStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);

  // Use signal-based inputs for proper reactivity
  listId = input<string>('');
  show = input<string[]>([]);
  blockClass = input<string>('');

  // Computed signal that properly reacts to listId changes
  listData: Signal<List> = computed(() => {
    const id = this.listId();
    return this.listStore.idToList()[id] as List;
  });
  roleMap: Signal<Record<string, string>> = this.mapService.collaboratorRoleMap;
  members: Signal<MembersModel> = computed(() => {
    return this.listData().members || { ownerEmail: '', memberUids: [], collaborators: {} } as MembersModel;
  });
  role: Signal<CollaboraterRole> = computed(() => {
    if (this.listData().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.listData().members) {
      return getRoleByUid(this.listData().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });
  lastUpdatedAtText: Signal<string> = computed(() => {
    return this.lastUpdatedByService.getLastUpdatedByString({
      lastUpdatedAt: this.listData().localUpdatedAt,
    });
  });

  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.listData().members);
  });

  // v150: isFav now comes from UserResources, not the list entity
  isFav: Signal<boolean> = computed(() => {
    const listId = this.listId();
    if (!listId) return false;
    return this.userResourceStore.isListFavorite(listId);
  });

  constructor(public ss: SettingsService, public cc: CacheService, public dialog: MatDialog, private mapService: MapService, private modalService: ModalService) { }

  getCompletedRange(listItems: Record<string, ListItem>): string {
    if (!listItems) {
      return this.ss.t('lists_listsSubContent');
    }
    const totalItems = Object.values(listItems).filter(item => !item.deletedAt).length;
    const doneItems = Object.values(listItems).filter(item => item?.done === true && !item.deletedAt).length;

    if (!totalItems || totalItems === 0) {
      return this.ss.t('lists_listsSubContent');
    }

    if (this.listData().addOnType === 'checkbox') {
      return `${doneItems}/${totalItems}`;
    } else {
      return `${totalItems}`;
    }
  }

  openListItemsDialog() {
    const confirmDialog = this.modalService.openLevel1(
      ListItemsComponent,
      {
        value: this.listData(),
        show: this.show
      },
      { disableClose: false, autoFocus: false }
    );
    // return confirmDialog.afterClosed();
  }
}
